import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AppBar, Box, Toolbar, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { EmptyState } from './EmptyState';

/**
 * What react-admin renders at `/login` (wired as `<Admin loginPage={...}>`
 * in `App.tsx`) whenever `checkAuth` finds no principal — the designed
 * "no active session" state, not a crash and not a blank page.
 *
 * This is deliberately **not** a login form: no field, no submit button, no
 * credential of any kind is ever collected here (the repository's hard
 * constraints forbid a local login screen — plan D-P). It exists purely so
 * the application has somewhere honest to land when unauthenticated: the
 * same brand wordmark every other screen shows (never a page title, per the
 * design system), and the same `EmptyState` pattern used for every other
 * designed empty/denied state in this application.
 *
 * Rendered outside `CustomLayout` (react-admin's login route is never
 * wrapped in the authenticated shell's menu/layout), so the wordmark bar is
 * reproduced here rather than shared — the alternative, relaxing
 * `requireAuth` so unauthenticated users see the full navigable shell,
 * would let every screen's own guard do this job instead, but at the cost
 * of exposing the whole menu/route surface before there is any session to
 * authorize it.
 */
export function NoSessionPage() {
  const translate = useTranslate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar>
          <Typography
            component="span"
            sx={{ whiteSpace: 'nowrap', fontSize: '1.05rem', letterSpacing: '-0.3px', lineHeight: 1 }}
          >
            <Box component="span" sx={{ color: (theme) => theme.custom.wordmarkBrandColor, fontWeight: 800 }}>
              {translate('pay.appBar.wordmarkBrand')}
            </Box>
            <Box
              component="span"
              sx={{ color: (theme) => theme.custom.wordmarkProductColor, fontWeight: 500, ml: 0.75 }}
            >
              {translate('pay.appBar.wordmarkProduct')}
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>
      <EmptyState
        icon={<LockOutlinedIcon fontSize="inherit" />}
        title={translate('pay.noSession.title')}
        body={translate('pay.noSession.body')}
      />
    </Box>
  );
}
